import { Component, DoCheck, EventEmitter, OnDestroy, OnInit, ViewEncapsulation, Input, Output } from '@angular/core';
import moment from 'moment';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';

import { DatacenterDateFilter, DynamicDatacenterFilter } from '../../../models/datacenter-filters';
import { pageTransitions } from '../../../animations/page-transitions';
import { DATE_CRITERION_TYPE, QUARTER_CRITERION_TYPE } from '../../../models/filter-formats';
import { CopoDatepickerInputDirective } from '../../../directives/date-picker.directive';

type DateRangeGroup = FormGroup<{ from: FormControl<Date | null>; to: FormControl<Date | null> }>;

@Component({
	selector: 'copo-date-filter',
	templateUrl: './view-date-filter.component.html',
	styleUrl: './view-date-filter.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		NgFor,
		MatIconModule,

		MatFormFieldModule,

		MatInputModule,
		CopoDatepickerInputDirective,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatButtonModule,
	]
})
export class ViewDateFilterComponent implements
	DynamicDatacenterFilter<DatacenterDateFilter>,
	OnInit,
	OnDestroy,
	DoCheck {
	@Input() filter?: DatacenterDateFilter;
	@Output() readonly removeFilter = new EventEmitter<void>();
	@Input() isDefaultFilter?: boolean;
	fieldName = '';
	translatedFieldName = '';
	formatType = '';
	filterType = '';

	readonly form = new FormGroup({
		values: new FormArray<DateRangeGroup>([])
	});

	private subscriptions = new Subscription();
	private lastKnownValuesRef: unknown = null;
	private lastKnownValuesLength = 0;

	get valuesFormArray() {
		return this.form.controls.values;
	}

	fromControl(index: number) {
		return this.valuesFormArray.at(index).controls.from;
	}

	toControl(index: number) {
		return this.valuesFormArray.at(index).controls.to;
	}

	ngOnInit() {
		if (!this.filter) {
			return;
		}
		this.formatType = this.filter.filterDateType;
		this.rebuildFormArray();
	}

	ngDoCheck() {
		// `filter.getValues()` can be mutated from outside this component.
		// Keep the rendered form controls in sync when rows are added/removed.
		if (!this.filter) {
			return;
		}
		const values = this.filter.getValues();
		if (values !== this.lastKnownValuesRef || values.length !== this.lastKnownValuesLength) {
			this.rebuildFormArray();
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	removeValue(index: number) {
		if (!this.filter) {
			return;
		}
		this.filter.getValues().splice(index, 1);
		this.rebuildFormArray();
		if (!this.filter.valueSize()) {
			this.removeFilter.emit();
		}
	}

	clearFrom(index: number) {
		this.fromControl(index).setValue(null);
	}

	clearTo(index: number) {
		this.toControl(index).setValue(null);
	}

	trackByFunc(index: number) {
		return index;
	}

	getPreparedFilterValues() {
		if (this.filter) {
			return {
				id: this.filter.filterId,
				values: this.filter.getValues().filter(filterValue => filterValue.to || filterValue.from)
			};
		}
		return undefined;
	}

	onStartDateChange(event: Date | null, index: number) {
		if (this.filter === undefined) {
			return;
		}
		if (event === null) {
			this.filter.getValues()[index].from = '';
			return;
		}

		if (this.filterType === DATE_CRITERION_TYPE) {
			if (this.filter.filterDateType === DATE_CRITERION_TYPE) {
				this.filter.getValues()[index].from = event;
				return;
			}
			if (this.filter.filterDateType === QUARTER_CRITERION_TYPE) {
				this.filter.getValues()[index].from = moment(event).startOf('quarter').toDate();
				return;
			}
		}
		this.filter.getValues()[index].from = this.filter.filterDateType === QUARTER_CRITERION_TYPE
			? moment(event).startOf('quarter').toDate()
			: moment(event).startOf('isoWeek').toDate();
	}

	onEndDateChange(event: Date | null, index: number) {
		if (this.filter === undefined) {
			return;
		}
		if (event === null) {
			this.filter.getValues()[index].to = '';
			return;
		}

		if (this.filterType === DATE_CRITERION_TYPE) {
			if (this.filter.filterDateType === DATE_CRITERION_TYPE) {
				this.filter.getValues()[index].to = event;
				return;
			}
			if (this.filter.filterDateType === QUARTER_CRITERION_TYPE) {
				this.filter.getValues()[index].to = moment(event).endOf('quarter').toDate();
				return;
			}
		}
		this.filter.getValues()[index].to = this.filter.filterDateType === QUARTER_CRITERION_TYPE
			? moment(event).endOf('quarter').toDate()
			: moment(event).endOf('isoWeek').toDate();
	}

	onTypeChange(type: 'date' | 'week') {
		if (this.filterType === type) {
			return;
		}
		this.filterType = type;
		// Re-emit current values so week/date normalization is applied immediately.
		for (const [index, group] of this.valuesFormArray.controls.entries()) {
			this.onControlChange(index, 'from', group.controls.from.value);
			this.onControlChange(index, 'to', group.controls.to.value);
		}
	}

	private rebuildFormArray() {
		if (!this.filter) {
			return;
		}

		const values = this.filter.getValues();
		this.lastKnownValuesRef = values;
		this.lastKnownValuesLength = values.length;

		this.subscriptions.unsubscribe();
		this.subscriptions = new Subscription();
		this.valuesFormArray.clear();

		for (const [index, value] of values.entries()) {
			// Values can be pushed as strings from other components.
			if (value.from && !(value.from instanceof Date)) {
				value.from = new Date(value.from);
			}
			if (value.to && !(value.to instanceof Date)) {
				value.to = new Date(value.to);
			}
			const group: DateRangeGroup = new FormGroup({
				from: new FormControl<Date | null>(value.from || null, { updateOn: 'blur' }),
				to: new FormControl<Date | null>(value.to || null, { updateOn: 'blur' }),
			});
			this.subscriptions.add(group.controls.from.valueChanges.subscribe((next: unknown) => {
				this.onControlChange(index, 'from', next);
			}));
			this.subscriptions.add(group.controls.to.valueChanges.subscribe((next: unknown) => {
				this.onControlChange(index, 'to', next);
			}));

			this.valuesFormArray.push(group);
		}
	}

	private onControlChange(index: number, key: 'from' | 'to', next: unknown) {
		if (!this.filter) {
			return;
		}
		if (index < 0 || index >= this.filter.getValues().length) {
			return;
		}
		if (index >= this.valuesFormArray.length) {
			return;
		}

		const toValidDateOrNull = (value: unknown): Date | null => {
			if (value instanceof Date) {
				return Number.isNaN(value.getTime()) ? null : value;
			}
			if (typeof value === 'string') {
				const parsed = new Date(value);
				return Number.isNaN(parsed.getTime()) ? null : parsed;
			}
			return null;
		};
		const isSameDateTime = (a: Date | null, b: unknown): boolean => {
			if (a === null && b === null) {
				return true;
			}
			if (!(a instanceof Date) || !(b instanceof Date)) {
				return false;
			}
			return a.getTime() === b.getTime();
		};

		const group = this.valuesFormArray.at(index);
		const control = key === 'from' ? group.controls.from : group.controls.to;
		const row = this.filter.getValues()[index];
		const currentModelValue = key === 'from' ? row.from || '' : row.to || '';

		if (typeof next === 'string') {
			if (next.trim() === '') {
				if (key === 'from') {
					this.onStartDateChange(null, index);
				} else {
					this.onEndDateChange(null, index);
				}
				control.setValue(null, { emitEvent: false });
				return;
			}
			// Invalid/partial manual typing: keep model unchanged and restore last valid value.
			control.setValue(
				currentModelValue === '' ? null : toValidDateOrNull(currentModelValue),
				{ emitEvent: false }
			);
			return;
		}

		if (next !== null && !(next instanceof Date)) {
			return;
		}
		if (next instanceof Date && Number.isNaN(next.getTime())) {
			return;
		}

		if (key === 'from') {
			this.onStartDateChange(next, index);
		} else {
			this.onEndDateChange(next, index);
		}

		const updatedModelValue = key === 'from' ? row.from || '' : row.to || '';
		const updatedControlValue = updatedModelValue === '' ? null : toValidDateOrNull(updatedModelValue);
		if (!isSameDateTime(updatedControlValue, next)) {
			control.setValue(updatedControlValue, { emitEvent: false });
		}
	}
}
